import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import CardActions from '@mui/material/CardActions';
import Chip from '@mui/material/Chip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import useAppNotifications from 'hooks/useAppNotifications';
import { markNotificationsRead } from 'api/notifications';

import { IconBell } from '@tabler/icons-react';

export default function NotificationSection() {
  const theme = useTheme();
  const downMD = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const { summary, loading, refresh, totalUnread, items } = useAppNotifications();

  const handleToggle = () => {
    setOpen((prev) => !prev);
  };

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const handleItemClick = async (item) => {
    setOpen(false);
    try {
      await markNotificationsRead({ keys: [item.key] });
      await refresh();
    } catch {
      /* navigate anyway */
    }
    if (item.href) navigate(item.href);
  };

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead({ all: true });
      await refresh();
    } catch {
      /* ignore */
    }
  };

  const handleViewBotQueue = () => {
    setOpen(false);
    navigate('/mattermost?section=needs-reply');
  };

  const hasActionItems = items.length > 0;

  return (
    <>
      <Box sx={{ ml: 2 }}>
        <Badge
          color="error"
          badgeContent={totalUnread}
          invisible={totalUnread === 0}
          max={99}
          overlap="circular"
        >
          <Avatar
            variant="rounded"
            sx={{
              ...theme.typography.commonAvatar,
              ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              color: totalUnread > 0 ? theme.vars.palette.error.dark : theme.vars.palette.warning.dark,
              background: totalUnread > 0 ? theme.vars.palette.error.light : theme.vars.palette.warning.light,
              animation: totalUnread > 0 ? 'pulse-notif 2s ease-in-out infinite' : 'none',
              '@keyframes pulse-notif': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                '50%': { boxShadow: '0 0 0 4px rgba(211, 47, 47, 0.25)' }
              },
              '&:hover, &[aria-controls="menu-list-grow"]': {
                color: theme.vars.palette.common.white,
                background: totalUnread > 0 ? theme.vars.palette.error.dark : theme.vars.palette.warning.dark
              },
              ...theme.applyStyles('dark', {
                background: theme.vars.palette.dark.main
              })
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
          >
            <IconBell stroke={1.5} size="20px" />
          </Avatar>
        </Badge>
      </Box>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={[{ name: 'offset', options: { offset: [downMD ? 5 : 0, 20] } }]}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper>
                {open && (
                  <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]} sx={{ maxWidth: 380 }}>
                    <Stack sx={{ gap: 2 }}>
                      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', pt: 2, px: 2 }}>
                        <Stack direction="row" sx={{ gap: 1, alignItems: 'center' }}>
                          <Typography variant="subtitle1">Notifications</Typography>
                          {totalUnread > 0 && (
                            <Chip
                              size="small"
                              label={totalUnread}
                              color="error"
                              variant="filled"
                              sx={{ color: 'common.white', height: 22 }}
                            />
                          )}
                        </Stack>
                        {totalUnread > 0 && (
                          <Typography
                            component="button"
                            variant="subtitle2"
                            onClick={handleMarkAllRead}
                            sx={{
                              color: 'primary.main',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              p: 0,
                              font: 'inherit'
                            }}
                          >
                            Mark all read
                          </Typography>
                        )}
                      </Stack>
                      <Box sx={{ maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden', '&::-webkit-scrollbar': { width: 5 } }}>
                        {loading && !summary ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : (
                          <>
                            <NotificationList items={items} onItemClick={handleItemClick} />
                            {!hasActionItems && !loading && (
                              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                                You&apos;re all caught up. Bot reply requests and onboarding items will appear here.
                              </Typography>
                            )}
                          </>
                        )}
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    </Stack>
                    <CardActions sx={{ p: 1.25, justifyContent: 'center', gap: 1 }}>
                      {summary?.stats?.bot_open > 0 && (
                        <Button size="small" variant="contained" color="error" onClick={handleViewBotQueue}>
                          Needs PHNX Reply ({summary.stats.bot_open})
                        </Button>
                      )}
                      <Button size="small" variant="text" onClick={() => refresh()}>
                        Refresh
                      </Button>
                    </CardActions>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
}
